import { TaskStatus } from './inspection-report.view-model';

export interface RgbColour {
  readonly r: number;
  readonly g: number;
  readonly b: number;
}

// Values the one-page MPVI print uses today (#00C853 / #FFD600 / #D50000 / black, lightgray rules).
const POINTS_PER_INCH = 72;

export const ReportTheme = {
  pageWidth: 8.5 * POINTS_PER_INCH,
  pageHeight: 11 * POINTS_PER_INCH,
  margin: 0.5 * POINTS_PER_INCH,
  footerHeight: 14,

  titleSize: 14,
  titleLineHeight: 18,
  headerSize: 9,
  headerLineHeight: 12,
  headerGap: 8,

  legendSize: 9,
  legendPadding: 4,
  legendEntryGap: 16,
  legendGap: 8,

  headingSize: 11,
  headingLineHeight: 14,
  headingRuleGap: 2,
  sectionGap: 10,

  rowSize: 9,
  rowLineHeight: 13,
  rowIndent: 4,
  iconSize: 10,
  iconGap: 4,

  columnGap: 16,

  treadLegendLineHeight: 13,
  treadLegendGap: 6,

  cardGap: 6,
  cardPadding: 4,
  cardTitleSize: 9,
  cardSize: 8,
  cardLineHeight: 11,

  footerSize: 8,
  ruleThickness: 0.5,
  underlineOffset: 1.5,
  underlineThickness: 0.6,

  quoteTitleGap: 8,
  quoteHeadingSize: 10,
  quoteHeadingLineHeight: 14,
  quoteSize: 9,
  quoteLineHeight: 12,
  quoteNoteSize: 8,
  quoteFieldIndent: 8,
  quoteDetailGap: 6,
  quoteTextColumnShare: 0.65,
  quoteTotalsColumnShare: 0.3,
  quoteDividerGap: 8,
  quoteCaseGap: 12,

  colours: {
    text: { r: 0, g: 0, b: 0 },
    muted: { r: 0.4, g: 0.4, b: 0.4 },
    rule: { r: 0.83, g: 0.83, b: 0.83 },
    border: { r: 0.6, g: 0.6, b: 0.6 },
    band: { r: 0.94, g: 0.94, b: 0.94 },
  },
  statusColours: {
    [TaskStatus.Green]: { r: 0, g: 0.784, b: 0.325 },
    [TaskStatus.Yellow]: { r: 1, g: 0.839, b: 0 },
    [TaskStatus.Red]: { r: 0.835, g: 0, b: 0 },
    [TaskStatus.Grey]: { r: 0, g: 0, b: 0 },
  } as Record<TaskStatus, RgbColour>,
} as const;

export const LEGEND_ENTRIES: readonly { readonly status: TaskStatus; readonly label: string }[] = [
  { status: TaskStatus.Green, label: 'Checked and OK' },
  { status: TaskStatus.Yellow, label: 'Future Attention Required' },
  { status: TaskStatus.Red, label: 'Repair Required' },
  { status: TaskStatus.Grey, label: 'Not Inspected' },
];

export const NOT_INSPECTED_LABEL = 'Not Inspected';
export const CONTINUED_SUFFIX = ' (continued)';

export const PSI_BEFORE_LABEL = 'PSI before:';
export const PSI_SET_TO_LABEL = 'PSI set to:';
export const TREAD_DEPTH_LABEL = 'Tread depth:';
export const WEAR_PATTERN_LABEL = 'Wear pattern/damage:';

export const QUOTE_DETAILS_TITLE = 'Quote Details';
export const QUOTE_CASE_LABEL = 'Case';
export const QUOTE_CASE_NAME_SEPARATOR = ' - ';
export const LABOUR_HOURS_LABEL = 'Labour Hours';
export const EXPIRES_LABEL = 'Expires';
export const EMPLOYEE_LABEL = 'Employee:';
export const CONCERN_LABEL = 'Concern';
export const CAUSE_LABEL = 'Cause';
export const CORRECTION_LABEL = 'Correction';
export const SUBTOTALS_LABEL = 'Subtotals';
export const LABOUR_SUBTOTAL_LABEL = 'Labour:';
export const PARTS_SUBTOTAL_LABEL = 'Parts:';
export const ADDITIONAL_CHARGES_LABEL = 'Additional Charges:';
export const TAX_LABEL = 'Tax:';
export const CASE_TOTAL_LABEL = 'Total:';
export const QUOTE_TOTAL_LABEL = 'Quote Total:';
export const SHOP_SUPPLIES_NOTE = '*Shop supplies may apply.';

// Material Icons glyphs on a 24x24 box: check_circle, warning, report, remove.
export const STATUS_ICON_PATHS: Record<TaskStatus, string> = {
  [TaskStatus.Green]:
    'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z',
  [TaskStatus.Yellow]: 'M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z',
  [TaskStatus.Red]:
    'M15.73 3H8.27L3 8.27v7.46L8.27 21h7.46L21 15.73V8.27L15.73 3zM12 17.3c-.72 0-1.3-.58-1.3-1.3 0-.72.58-1.3 1.3-1.3.72 0 1.3.58 1.3 1.3 0 .72-.58 1.3-1.3 1.3zm1-4.3h-2V7h2v6z',
  [TaskStatus.Grey]: 'M19 13H5v-2h14v2z',
};
export const STATUS_ICON_BOX = 24;
